/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { AuthenticationService, HttpCacheService, I18nService, Logger } from '@app/core';
import { WebSocketsService } from '@app/core/websockets/websockets.service';
import { SideMenuComponent } from '@app/shell/side-menu/side-menu.component';
import { SalesFormService } from '@app/sales/sales/sales-form.service';
import { InvoicesFormService } from '@app/finance/invoices/invoices-form.service';
import { HotelBookingsFormService } from '@app/hotels/hotel-bookings/hotel-bookings-form.service';
import { EventsFormService } from '@app/events/events/events-form.service';
import { HotelsFormService } from '@app/hotels/hotels/hotels-form.service';
import { EventGroupsFormService } from '@app/events/events-groups/event-groups-form.service';
import { VenuesFormService } from '@app/settings/venues/venues-form.service';
import { ClientsFormService } from '@app/clients/clients/clients-form.service';
import { SuppliersFormService } from '@app/suppliers/suppliers/suppliers-form.service';
import { AssignmentsService } from '@app/shared/services/assignments.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DataSet } from '@app/shared/kendo/data-set';
import { PaymentsFormService } from '@app/finance/payments/payments-form.service';
import { User } from '@app/settings/users/user.model';
import { environment } from '@env/environment';
import { interval } from 'rxjs';
import { skip } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RemindersFormService } from '@app/reminders/reminders/reminders-form.service';
import { RemindersService } from '@app/reminders/reminders/reminders.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Reminder } from '@app/reminders/reminders/reminder.model';
import { ProfileFormService } from '@app/profile/profile-form.service';
import { NotificationsService } from '@app/notifications/notifications/notifications.service';
import { Notification } from '@app/notifications/notifications/notification.model';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { AccessService } from '@app/core/access/access.service';
import { NgScrollbar } from 'ngx-scrollbar';
import { CheckInOut } from '@app/reports/check-in-out/check-in-out.model';
import { CheckInOutService } from '@app/reports/check-in-out/check-in-out.service';

const log = new Logger('HeaderComponent');

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() sideMenu: SideMenuComponent;
  @ViewChild('notificationsScroll') notificationsScroll?: NgScrollbar;
  @ViewChild('remindersScroll') remindersScroll?: NgScrollbar;
  assignments: any = null;
  reminders: Array<Reminder> = [];
  notifications: Array<Notification> = [];
  remindersCount: number | string = 0;
  notificationsCount: number | string = 0;
  checkInOutStatus: CheckInOut = null;
  checkInOutLoading = false;
  checkInOutMinutesLabel = '';
  env = environment;
  private checkInOutIntervalId: any = null;
  private checkInOutCurrentDay = moment().format('YYYY-MM-DD');

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private i18nService: I18nService,
    private websockets: WebSocketsService,
    private salesFormService: SalesFormService,
    private invoicesFormService: InvoicesFormService,
    private hotelBookingsFormService: HotelBookingsFormService,
    private eventsFormService: EventsFormService,
    private hotelFormService: HotelsFormService,
    private eventGroupsFormService: EventGroupsFormService,
    private venuesFormService: VenuesFormService,
    private clientsFormService: ClientsFormService,
    private suppliersFormService: SuppliersFormService,
    private assignmentsService: AssignmentsService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private paymentsFormService: PaymentsFormService,
    private remindersFormService: RemindersFormService,
    private remindersService: RemindersService,
    private notificationsService: NotificationsService,
    private httpCacheService: HttpCacheService,
    private profileFormService: ProfileFormService,
    private accessService: AccessService,
    private checkInOutService: CheckInOutService
  ) {}

  get currentLanguage(): string {
    return this.i18nService.language;
  }

  get languages(): string[] {
    return this.i18nService.supportedLanguages;
  }

  get username(): string | null {
    const accessToken = this.authenticationService.accessToken;
    log.debug('accessToken', accessToken.profile);
    // return accessToken ? accessToken.username : null;
    return null;
  }

  get user(): User {
    if (!this.authenticationService.accessToken.profile) {
      return null;
    }
    return new User().deserialize(this.authenticationService.accessToken.profile);
  }

  toggleMenu() {
    this.sideMenu.toggleMenu();
  }

  ngOnInit() {
    if (this.canUseCheckInOut()) {
      this.loadCheckInOutStatus();
    }

    this.getReminders();
    this.getNotifications();
    this.refreshReminders();

    this.websockets.reminderNotifStream.pipe(skip(1), untilDestroyed(this)).subscribe((res: string) => {
      log.debug('res', res);
      this.getReminders();
    });
  }

  ngOnDestroy(): void {
    this.clearCheckInOutTicker();
  }

  setLanguage(language: string) {
    this.i18nService.language = language;
  }

  canUseCheckInOut(): boolean {
    return !!this.user;
  }

  loadCheckInOutStatus(): void {
    this.checkInOutLoading = true;

    this.checkInOutService.status().subscribe(
      (status: CheckInOut) => {
        this.setCheckInOutStatus(status);
        this.checkInOutLoading = false;
      },
      () => {
        this.checkInOutLoading = false;
      }
    );
  }

  toggleCheckInOut(): void {
    if (this.checkInOutLoading) {
      return;
    }

    this.checkInOutLoading = true;

    this.checkInOutService.toggle().subscribe(
      (status: CheckInOut) => {
        this.setCheckInOutStatus(status);
        this.checkInOutLoading = false;
      },
      () => {
        this.checkInOutLoading = false;
      }
    );
  }
  private refreshCheckInOutTicker(): void {
    const today = moment().format('YYYY-MM-DD');

    if (today !== this.checkInOutCurrentDay) {
      this.checkInOutCurrentDay = today;
      this.loadCheckInOutStatus();
      return;
    }

    this.refreshCheckInOutMinutesLabel();
  }

  logout() {
    this.authenticationService.logout().subscribe(() => {
      this.websockets.disconnect();
      this.router.navigate(['/login'], { replaceUrl: true });
    });
  }

  createSale() {
    this.salesFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createInvoice() {
    this.invoicesFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createBooking() {
    this.hotelBookingsFormService.open().then(
      (response) => {
        log.info('modal closed!');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createEvent() {
    this.eventsFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createHotel() {
    this.hotelFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createEventGroup() {
    this.eventGroupsFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createVenue() {
    this.venuesFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createClient() {
    this.clientsFormService.open().then(
      (response) => {
        log.info('modal closed');
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  createSupplier() {
    this.suppliersFormService.open().then(
      (response) => {
        log.info(response);
      },
      (err) => {
        log.info(err);
      }
    );
  }

  assignmentsDropdownToggle(opened: boolean) {
    if (opened) {
      this.getAssignments();
    }
  }

  getAssignments(showToastr: boolean = false) {
    const oldAssignments = this.assignments;
    this.assignmentsService.getAll().subscribe((res: any) => {
      for (const key in res) {
        if (key && res[key] === null) {
          delete res[key];
        }
      }

      this.assignments = res;

      if (showToastr && JSON.stringify(oldAssignments) !== JSON.stringify(this.assignments)) {
        this.translate.get('HEADER.ASSIGNMENTS.ASSIGNMENTS_UPDATED').subscribe((trans: string) => {
          this.toastr.success(trans);
        });
      }
    });
  }

  refreshReminders(refreshInterval: number = 600000) {
    interval(refreshInterval)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.getReminders();
        this.getNotifications();
      });
  }

  createPayment() {
    this.paymentsFormService.open().then(
      (response) => {
        log.info(response);
      },
      (err) => {
        log.info(err);
      }
    );
  }

  assignmentClick(assignment: any) {
    log.debug('assignment', assignment);

    switch (assignment.key) {
      case 'waiting_enquiries': {
        //  this.router.navigate(['/enquiries'], { queryParams: { finished: false }, replaceUrl: true });
        this.router.navigate(['/enquiries'], {
          queryParams: {
            dataSet: new DataSet().addFilter('finished', false).getStringifyDataSet(),
          },
        });
        break;
      }
      case 'waiting_hotel_booking_cancellations': {
        this.router.navigate(['/hotel-bookings'], {
          queryParams: {
            dataSet: new DataSet().addFilter('assignmentToCancellation', true).getStringifyDataSet(),
          },
        });
        break;
      }
      case 'waiting_hotel_booking_payments': {
        this.router.navigate(['/hotel-bookings'], {
          queryParams: {
            dataSet: new DataSet().addFilter('assignmentToPayment', true).getStringifyDataSet(),
          },
        });
        break;
      }
      case 'waiting_sales_to_issue_invoice': {
        this.router.navigate(['/sales'], {
          queryParams: {
            finals_to_issue_invoice: true,
          },
        });
        break;
      }
      default: {
        this.translate.get('HEADER.ASSIGNMENTS.UNKNOWN_TYPE').subscribe((trans: string) => {
          this.toastr.error(trans);
        });
        break;
      }
    }
  }

  /**
   * Get reminders list for the current user, filtered by date
   */
  getReminders(): void {
    const dataSet = new DataSet().addFilter('remind_at', moment().format(this.env.apiPipeDateTimeFormat), 'lt');

    const apiQuery = new ApiQuery()
      .addFilters({
        user_id: this.user.id,
        completed: false,
      })
      .setDataSetFilters(dataSet);

    this.remindersService.index(apiQuery).subscribe((reminders) => {
      this.reminders = reminders.data;
      this.remindersCount = reminders.meta.total > 99 ? '+99' : reminders.meta.total;

      this.updateDropdownScrollbar(this.remindersScroll);
    });
  }

  /**
   * Open reminder's form
   *
   * @param reminder Reminder to open
   */
  openReminder(reminder: Reminder): void {
    this.remindersFormService.open(reminder).then(
      () => {},
      () => {}
    );
  }

  /**
   * Get all notifications
   */
  getNotifications(): void {
    const apiQuery = new ApiQuery().addFilter('unread', true);

    this.notificationsService.index(apiQuery).subscribe((notifications: PaginatedResponse) => {
      this.notifications = notifications.data;
      this.notificationsCount = notifications.meta.total;

      this.updateDropdownScrollbar(this.notificationsScroll);
    });
  }

  /**
   * Redirect to notification and mark as read
   *
   * @param notification Notification
   */
  openNotification(notification: Notification) {
    this.notificationsService.update(notification).subscribe(() => {
      if (notification.link) {
        window.open(notification.link, '_blank');
      }
      this.getNotifications();
    });
  }

  /**
   * Open user's profile modal form
   */
  openProfile() {
    this.profileFormService.open().then(
      () => {},
      () => {}
    );
  }

  cacheClear() {
    this.httpCacheService.cleanCache();
    this.accessService.clearStorage();

    this.translate.get('HEADER.CACHE_CLEARED_MSG').subscribe((trans: string) => {
      this.toastr.success(trans);
    });
  }

  clearFilters() {
    for (const key in window.localStorage) {
      if (key) {
        if (key.startsWith('FILTERSTATE')) {
          window.localStorage.removeItem(key);
        }
      }
    }
  }

  onRemindersDropdownOpenChange(opened: boolean): void {
    if (!opened) {
      return;
    }

    this.getReminders();

    this.updateDropdownScrollbar(this.remindersScroll);
  }

  onNotificationsDropdownOpenChange(opened: boolean): void {
    if (!opened) {
      return;
    }

    this.getNotifications();

    this.updateDropdownScrollbar(this.notificationsScroll);
  }

  private updateDropdownScrollbar(scrollbar: NgScrollbar | undefined): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollbar?.update();
      });
    });
  }

  private setCheckInOutStatus(status: CheckInOut): void {
    this.checkInOutStatus = status;
    this.refreshCheckInOutMinutesLabel();

    // Keep the ticker active even when checked out so the header resets at midnight.
    this.startCheckInOutTicker();
  }

  private refreshCheckInOutMinutesLabel(): void {
    if (!this.checkInOutStatus) {
      this.checkInOutMinutesLabel = '';
      return;
    }

    this.checkInOutMinutesLabel = this.formatMinutes(this.getCurrentWorkedMinutes());
  }

  private getCurrentWorkedMinutes(): number {
    if (!this.checkInOutStatus) {
      return 0;
    }

    if (this.checkInOutStatus.is_open && this.checkInOutStatus.open_interval_started_at?.date) {
      return (
        this.checkInOutStatus.total_minutes +
        moment().diff(moment(this.checkInOutStatus.open_interval_started_at.date), 'minutes')
      );
    }

    return this.checkInOutStatus.current_total_minutes || this.checkInOutStatus.total_minutes || 0;
  }

  private formatMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const restMinutes = Math.round(minutes % 60);

    return hours + 'h ' + restMinutes + 'm';
  }

  private startCheckInOutTicker(): void {
    this.clearCheckInOutTicker();

    this.checkInOutIntervalId = setInterval(() => {
      this.refreshCheckInOutTicker();
    }, 30000);
  }

  private clearCheckInOutTicker(): void {
    if (this.checkInOutIntervalId) {
      clearInterval(this.checkInOutIntervalId);
      this.checkInOutIntervalId = null;
    }
  }
}
